import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { QueryParamItem } from './QueryParamItem';

import { FormGroup } from '@/components/FormGroup';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/Select';
import { useGetPromptVariables } from '@/hooks/useGetPromptVariables';
import {
  Prompt,
  PromptVariable,
  RenderPromptDto,
} from '@/services/PromptsService';

const forbiddenSQLPattern =
  /\b(SELECT|FROM|WHERE|AND|OR|IN|LIKE|LIMIT|JOIN|ON|GROUP BY|ORDER BY|DELETE|INSERT|UPDATE|DROP|EXEC|;|--|\*|\/\*)\b/i;

const promptSchema = z.object({
  paramKey: z.string().optional(),
  paramValue: z.string().optional(),
});

type PromptFormData = z.infer<typeof promptSchema>;

interface QueryParamsCardProps {
  onPayload: (payload: RenderPromptDto) => void;
  prompt?: Prompt;
  queryParams: Record<string, string | number | boolean>;
  onQueryParamsChange: (
    params: Record<string, string | number | boolean>,
  ) => void;
  promptVariables?: PromptVariable[];
  query?: string;
}

export function QueryParamsCard({
  onPayload,
  prompt,
  queryParams,
  onQueryParamsChange,
  promptVariables: externalPromptVariables,
  query,
}: QueryParamsCardProps) {
  const { control, reset, getValues } = useForm<PromptFormData>({
    resolver: zodResolver(promptSchema),
    defaultValues: {
      paramKey: '',
      paramValue: '',
    },
  });

  const { promptVariables: hookPromptVariables } = useGetPromptVariables({
    name: !externalPromptVariables && prompt?.variables ? prompt.variables : [],
  });

  const promptVariables = externalPromptVariables || hookPromptVariables;

  const extractVariablesFromValues = useCallback(
    (promptVariables: PromptVariable[]) => {
      const variablePattern = /\{\{([^}]+)\}\}/g;
      const extractedVariables = new Set<string>();

      promptVariables?.forEach((variable) => {
        if (variable.query) {
          let match;
          while ((match = variablePattern.exec(variable.query)) !== null) {
            extractedVariables.add(match[1].trim());
          }
        }
      });

      return Array.from(extractedVariables).sort();
    },
    [],
  );

  const extractVariablesFromQuery = useCallback((query: string) => {
    const variablePattern = /\{\{([^}]+)\}\}/g;
    const extractedVariables = new Set<string>();

    let match;
    while ((match = variablePattern.exec(query)) !== null) {
      extractedVariables.add(match[1].trim());
    }

    return Array.from(extractedVariables).sort();
  }, []);

  const availableVariables = useMemo(() => {
    if (query) {
      return extractVariablesFromQuery(query);
    } else if (prompt && promptVariables.length > 0) {
      return extractVariablesFromValues(promptVariables);
    }
    return [];
  }, [
    query,
    prompt,
    promptVariables,
    extractVariablesFromQuery,
    extractVariablesFromValues,
  ]);

  const hasVariables = availableVariables.length > 0;

  useEffect(() => {
    onPayload({
      promptName: prompt?.promptName || '',
      serviceName: prompt?.serviceName || '',
      customerId: prompt?.customerId ?? undefined,
      version: prompt?.version || '',
      queryParams:
        Object.keys(queryParams).length > 0 ? queryParams : undefined,
    });
  }, [queryParams, prompt, onPayload]);

  const handleAddQueryParams = useCallback(() => {
    const { paramKey, paramValue } = getValues();
    if (!paramKey || !paramValue) return;

    if (
      forbiddenSQLPattern.test(paramKey) ||
      forbiddenSQLPattern.test(paramValue)
    ) {
      toast.error('Parameter key or value contains invalid characters');
      return;
    }

    onQueryParamsChange({
      ...queryParams,
      [paramKey]: paramValue,
    });

    reset({
      paramKey: '',
      paramValue: '',
    });
  }, [getValues, reset, queryParams, onQueryParamsChange]);

  function handleDeleteQueryParams(key: string) {
    const updated = { ...queryParams };
    delete updated[key];
    onQueryParamsChange(updated);
  }

  const hasQueryParams = Boolean(Object.keys(queryParams).length);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-md flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Query Parameters
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        <FormGroup>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <RequiredLabel htmlFor="paramKey">Variable</RequiredLabel>
              <Controller
                name="paramKey"
                control={control}
                render={({ field }) => (
                  <>
                    {hasVariables ? (
                      // Mostra SELECT se há variáveis disponíveis
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger id="paramKey">
                          <SelectValue placeholder="Selecione uma variável" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableVariables.map((variable) => (
                            <SelectItem key={variable} value={variable}>
                              {variable}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      // Mostra INPUT se não há variáveis
                      <Input
                        {...field}
                        id="paramKey"
                        placeholder="Digite o nome da variável"
                        onKeyPress={(e) =>
                          e.key === 'Enter' && handleAddQueryParams()
                        }
                      />
                    )}
                  </>
                )}
              />
            </div>

            <div className="space-y-2">
              <RequiredLabel htmlFor="paramValue">Value</RequiredLabel>
              <Controller
                name="paramValue"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="paramValue"
                    placeholder="e.g., 123"
                    onKeyPress={(e) =>
                      e.key === 'Enter' && handleAddQueryParams()
                    }
                  />
                )}
              />
            </div>

            <div className="flex flex-col justify-end space-y-1">
              <div className="hidden h-5"></div>
              <Button
                type="button"
                onClick={handleAddQueryParams}
                className="w-full gap-2"
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
          </div>
        </FormGroup>

        {hasQueryParams && (
          <div className="space-y-3">
            <div className="border-t pt-4">
              <h4 className="mb-3 text-sm font-medium text-gray-700">
                Current Parameters:
              </h4>
              <div className="space-y-2">
                {Object.entries(queryParams).map(([key, value]) => (
                  <QueryParamItem
                    key={key}
                    paramKey={key}
                    paramValue={value}
                    onDelete={() => handleDeleteQueryParams(key)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
